import { createElement } from 'react';
import {
  useCanAccess,
  useCreatePath,
  useGetResourceLabel,
  useHasDashboard,
  useResourceDefinitions,
  useTranslate,
} from 'ra-core';
import { Link, matchPath, useLocation, useMatch } from 'react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { House } from 'lucide-react';
import { CirclesThreeIcon, ChartLineUpIcon } from '@phosphor-icons/react';
import { UserMenu } from '@/components/admin/user-menu.tsx';
import { ThemeToggle } from '@/components/admin/theme-toggle.tsx';
import { Button } from '@/components/ui/button.tsx';
import GolootLogo from '@/components/ui/GolootLogo.tsx';

export function AppSidebar() {
  const hasDashboard = useHasDashboard();
  const resources = useResourceDefinitions();
  const { openMobile, setOpenMobile, open, setOpen } = useSidebar();

  const handleMenuItemClick = () => {
    if (openMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="p-0 mb-3 group-data-[collapsible=icon]:mb-0 group-data-[collapsible=icon]:py-4">
        {open ? (
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/" className="shrink-0">
              <GolootLogo className="border rounded-sm" />
            </Link>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight tracking-tight">
                Goloot
              </div>
              <div className="text-xs leading-4 tracking-tight">
                for Advertisers
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(true)}
              className="h-10 w-10 p-0"
            >
              <GolootLogo className="border rounded-sm" />
            </Button>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <SidebarMenu>
              {hasDashboard ? (
                <DashboardMenuItem onClick={handleMenuItemClick} />
              ) : null}
              {Object.keys(resources)
                .filter(name => resources[name].hasList)
                .map(name => (
                  <ResourceMenuItem
                    key={name}
                    name={name}
                    onClick={handleMenuItemClick}
                    matchPaths={
                      name === 'campaigns'
                        ? [
                            '/advertiser/:advertiserId/campaigns',
                            '/advertiser/:advertiserId/campaign',
                          ]
                        : undefined
                    }
                  />
                ))}
              <SidebarMenuItem>
                <SidebarMenuButton
                  disabled
                  className="text-muted-foreground"
                  tooltip="Coming soon"
                >
                  <ChartLineUpIcon />
                  <span className="group-data-[collapsible=icon]:hidden">
                    Reporting
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {/* Pinned to the bottom of the nav area (mt-auto), so it sits above the
            user/advertiser-select zone in the footer. */}
        <SidebarGroup className="mt-auto px-0 group-data-[collapsible=icon]:p-0">
          <SidebarGroupContent>
            <ThemeToggle />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="px-0 pb-0 group-data-[collapsible=icon]:py-4">
        <UserMenu />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

export const DashboardMenuItem = ({ onClick }: { onClick?: () => void }) => {
  const translate = useTranslate();
  const label = translate('ra.page.dashboard', {
    _: 'Dashboard',
  });
  const match = useMatch({ path: '/', end: true });
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!match}>
        <Link to="/" onClick={onClick}>
          <House />
          <span className="group-data-[collapsible=icon]:hidden">{label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export const ResourceMenuItem = ({
  name,
  onClick,
  matchPaths,
}: {
  name: string;
  onClick?: () => void;
  matchPaths?: string[];
}) => {
  const { canAccess, isPending } = useCanAccess({
    resource: name,
    action: 'list',
  });
  const resources = useResourceDefinitions();
  const getResourceLabel = useGetResourceLabel();
  const createPath = useCreatePath();
  const { pathname } = useLocation();
  const to = createPath({
    resource: name,
    type: 'list',
  });
  const isActive = !!(
    matchPath({ path: to, end: false }, pathname) ||
    matchPaths?.some(p => matchPath({ path: p, end: false }, pathname))
  );

  if (isPending) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (!resources || !resources[name] || !canAccess) return null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link to={to} state={{ _scrollToTop: true }} onClick={onClick}>
          {resources[name].icon ? (
            createElement(resources[name].icon)
          ) : (
            // Campaign icon
            <CirclesThreeIcon />
          )}
          <span className="group-data-[collapsible=icon]:hidden">
            {getResourceLabel(name, 2)}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
